import { useMemo, useState } from "react";
import { C, PageHeader, GlassCard, Badge, Skeleton, statusColor } from "@coremonit/design-system";
import { Network, Zap } from "lucide-react";
import { useDependencyGraph } from "../api/hooks";
import type { DependencyGraph } from "@coremonit/types";

// Fixed layered layout, grouped by node kind.
const LAYERS: Array<{ kind: string; label: string }> = [
  { kind: "frontend",  label: "Frontend" },
  { kind: "gateway",   label: "Gateway" },
  { kind: "auth",      label: "Auth" },
  { kind: "backend",   label: "Services" },
  { kind: "datastore", label: "Data" },
  { kind: "cache",     label: "Cache" },
  { kind: "queue",     label: "Queue" },
  { kind: "external",  label: "External" },
];

const NODE_W = 156;
const NODE_H = 60;
const LAYER_GAP = 210;
const NODE_GAP = 14;

interface Layout {
  nodes: Array<{ id: string; name: string; status: string; kind: string; x: number; y: number }>;
  edges: DependencyGraph["edges"];
  width: number;
  height: number;
}

function layout(graph: DependencyGraph): Layout {
  const grouped: Record<string, DependencyGraph["services"]> = {};
  for (const s of graph.services) {
    (grouped[s.kind] ??= []).push(s);
  }
  const nodes: Layout["nodes"] = [];
  let maxLayerHeight = 0;
  LAYERS.forEach((layer, li) => {
    const g = grouped[layer.kind] ?? [];
    const yStart = 60;
    g.forEach((s, i) => {
      const x = li * LAYER_GAP + 40;
      const y = yStart + i * (NODE_H + NODE_GAP);
      nodes.push({ id: s.id, name: s.name, status: s.status, kind: s.kind, x, y });
    });
    maxLayerHeight = Math.max(maxLayerHeight, yStart + g.length * (NODE_H + NODE_GAP));
  });
  return {
    nodes,
    edges: graph.edges,
    width: LAYERS.length * LAYER_GAP + 60,
    height: maxLayerHeight + 40,
  };
}

export function ServiceMapPage() {
  const g = useDependencyGraph();
  const [hover, setHover] = useState<string | null>(null);
  const [hoverEdge, setHoverEdge] = useState<DependencyGraph["edges"][number] | null>(null);

  const l = useMemo(() => (g.data ? layout(g.data) : null), [g.data]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<Network size={16} />}
        title="Service Map"
        subtitle="Interactive topology · animated traffic · hover for latency, errors, RPM"
        actions={<Badge color="blue"><Zap size={9} /> live</Badge>}
      />
      <GlassCard style={{ padding: 12, overflow: "hidden" }}>
        {!l ? <Skeleton height={420} /> : (
          <div style={{ position: "relative", overflow: "auto" }}>
            <svg width={l.width} height={l.height} style={{ display: "block" }}>
              <defs>
                <marker id="arrowhead" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={C.borderBright} />
                </marker>
                <linearGradient id="edge-blue" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={C.blue} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={C.cyan} stopOpacity="0.35" />
                </linearGradient>
              </defs>

              {/* Layer labels */}
              {LAYERS.map((layer, li) => (
                <text key={layer.kind} x={li * LAYER_GAP + 40 + NODE_W / 2} y={22} fill={C.textMuted} fontSize={9} textAnchor="middle" style={{ textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>
                  {layer.label}
                </text>
              ))}

              {/* Edges */}
              {l.edges.map((e, i) => {
                const from = l.nodes.find((n) => n.id === e.fromServiceId);
                const to = l.nodes.find((n) => n.id === e.toServiceId);
                if (!from || !to) return null;
                const x1 = from.x + NODE_W;
                const y1 = from.y + NODE_H / 2;
                const x2 = to.x;
                const y2 = to.y + NODE_H / 2;
                const mx = (x1 + x2) / 2;
                const path = `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
                const active = hover === from.id || hover === to.id;
                const color = e.errorRatePct > 2 ? C.red : e.critical ? C.blue : C.borderBright;
                return (
                  <g key={i} onMouseEnter={() => setHoverEdge(e)} onMouseLeave={() => setHoverEdge(null)}>
                    <path d={path} stroke={color} strokeWidth={active ? 2.4 : 1.4} fill="none" markerEnd="url(#arrowhead)" opacity={active ? 1 : 0.55} />
                    {/* Traffic dash */}
                    <path
                      d={path}
                      stroke={color}
                      strokeWidth={1}
                      strokeDasharray="6 8"
                      fill="none"
                      opacity={0.7}
                      style={{ animation: "cm-flow-dash 3s linear infinite" }}
                    />
                  </g>
                );
              })}

              {/* Nodes */}
              {l.nodes.map((n) => {
                const isHover = hover === n.id;
                const c = statusColor(n.status);
                return (
                  <g key={n.id} transform={`translate(${n.x}, ${n.y})`} onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }}>
                    <rect
                      width={NODE_W} height={NODE_H} rx={12}
                      fill={C.surface}
                      stroke={isHover ? C.borderBright : C.border}
                      strokeWidth={isHover ? 1.6 : 1}
                      style={{ filter: isHover ? `drop-shadow(0 0 12px ${c}88)` : "none", transition: "all 0.2s" }}
                    />
                    <circle cx={16} cy={NODE_H / 2} r={5} fill={c} style={{ filter: `drop-shadow(0 0 6px ${c})` }} />
                    {n.status !== "healthy" && (
                      <circle cx={16} cy={NODE_H / 2} r={9} fill={c} opacity={0.3} style={{ animation: "cm-halo-pulse 1.8s ease-in-out infinite" }} />
                    )}
                    <text x={30} y={NODE_H / 2 - 4} fill={C.text} fontSize={12} fontWeight={600}>
                      {n.name.length > 16 ? n.name.slice(0, 15) + "…" : n.name}
                    </text>
                    <text x={30} y={NODE_H / 2 + 12} fill={C.textMuted} fontSize={9} style={{ textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>
                      {n.kind}
                    </text>
                  </g>
                );
              })}
            </svg>

            {hoverEdge && (
              <div style={{ position: "absolute", top: 12, right: 12, padding: 12, background: C.surface, border: `1px solid ${C.borderBright}`, borderRadius: 12, minWidth: 220, boxShadow: `0 0 20px rgba(45,127,249,0.2)` }}>
                <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>Dependency</div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600, marginTop: 2 }}>{hoverEdge.fromServiceId} → {hoverEdge.toServiceId}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8, fontSize: 10 }}>
                  <span style={{ color: C.textMuted }}>Latency p95</span><span style={{ color: C.text }}>{hoverEdge.latencyP95Ms}ms</span>
                  <span style={{ color: C.textMuted }}>Error rate</span><span style={{ color: hoverEdge.errorRatePct > 1 ? C.red : C.green }}>{hoverEdge.errorRatePct}%</span>
                  <span style={{ color: C.textMuted }}>Requests / min</span><span style={{ color: C.text }}>{hoverEdge.requestsPerMin.toLocaleString()}</span>
                  <span style={{ color: C.textMuted }}>Critical path</span><span style={{ color: hoverEdge.critical ? C.blue : C.textMuted }}>{hoverEdge.critical ? "yes" : "no"}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </GlassCard>
    </div>
  );
}
